import { create } from 'zustand';

import { AuthUser } from './schemas';

export type SessionStatus = 'unknown' | 'authenticated' | 'guest';

interface SessionState {
  status: SessionStatus;
  user: AuthUser | null;
  /**
   * Access token lives IN MEMORY ONLY (page refresh -> re-auth via refresh
   * flow). Never persisted to localStorage/sessionStorage.
   */
  accessToken: string | null;
  /**
   * Refresh token is held IN MEMORY ONLY (never persisted) — the pilot stopgap. The full
   * hardening moves it to an httpOnly, SameSite cookie SET BY Identity (with CORS credentials +
   * CSRF handling); that is a backend+FE auth change tracked in the security band (C-9c → D-5
   * interactive OIDC / D-4 security program), deliberately NOT bolted on here [STD-DRIFT].
   */
  refreshToken: string | null;
  setSession: (user: AuthUser, accessToken: string, refreshToken?: string) => void;
  setTokens: (accessToken: string, refreshToken?: string) => void;
  clearSession: () => void;
}

/** UI/session state via Zustand — not Redux [STD-FE] */
export const useSessionStore = create<SessionState>()((set) => ({
  status: 'unknown',
  user: null,
  accessToken: null,
  refreshToken: null,
  setSession: (user, accessToken, refreshToken) =>
    set({ status: 'authenticated', user, accessToken, refreshToken: refreshToken ?? null }),
  setTokens: (accessToken, refreshToken) =>
    set((state) => ({ accessToken, refreshToken: refreshToken ?? state.refreshToken })),
  clearSession: () => set({ status: 'guest', user: null, accessToken: null, refreshToken: null })
}));

/** non-hook accessor for the api-client's getAccessToken option */
export function getAccessToken(): string | null {
  return useSessionStore.getState().accessToken;
}
